import { Controller, FieldErrors, useFormContext } from 'react-hook-form';
import { maxLengthRule, patternRule, requiredText, trimString } from '#/Scripts/shared/form/validation';
import { MONTHLY_PAPER_CHECK_COUNT_OPTIONS, PLAN_OPTIONS } from '#/Scripts/shared/signupOptions';
import AccountPlan from '#/Scripts/sign-up/components/AccountPlanCard/AccountPlanCard';
import { InputError } from '#/Scripts/sign-up/components/shared/InputError';
import Button from '#sign-up/components/common/Button/Button';
import { ButtonVariants } from '#sign-up/components/common/Button/buttonTypes';
import CheckBox from '#sign-up/components/common/CheckBox/CheckBox';
import FormTextField from '#sign-up/components/common/FormTextField/FormTextField';
import Select from '#sign-up/components/common/Select/Select';
import {
  enumToDropdownOptions,
  IntegrationType,
  IntegrationTypeDisplayNames,
  planTypeToString,
  SignupFormValues,
  stringToPlanType,
} from '#sign-up/components/SignUp/types';

const managementSystemOptions = enumToDropdownOptions(IntegrationType, IntegrationTypeDisplayNames);

export const AccountInformationForm = () => {
  const form = useFormContext<SignupFormValues>();
  const integrationTypeId = form.watch('IntegrationTypeId');
  const isOther = integrationTypeId === IntegrationType.Other;

  return (
    <div className="signup-form bank-information">
      <div className="signup-form-row">
        <div style={{ display: 'flex', gap: '1rem', width: '100%', minWidth: 0 }}>
          <Controller
            control={form.control}
            name="IntegrationTypeId"
            rules={{
              ...requiredText('Management System'),
            }}
            render={({ field }) => (
              <Select
                label="Management System"
                placeholder="Select..."
                options={managementSystemOptions}
                value={field.value === undefined || field.value === null ? '' : String(field.value)}
                onChange={(e) => {
                  const v = e.target.value;
                  const newType = v === '' ? undefined : Number.parseInt(v, 10);
                  field.onChange(newType);
                  if (newType !== IntegrationType.Other) {
                    form.setValue('OtherManagementSystem', null, { shouldValidate: false });
                  }
                }}
                onBlur={field.onBlur}
                name={field.name}
                errors={form.formState.errors as FieldErrors}
                className="with-subtext"
              />
            )}
          />
          {isOther && (
            <FormTextField
              label="Please Specify"
              className="with-subtext"
              {...form.register('OtherManagementSystem', {
                ...requiredText('Management System'),
                ...maxLengthRule(150, 'Management System'),
                ...trimString,
              })}
              errors={form.formState.errors.OtherManagementSystem}
            />
          )}
        </div>
        <Controller
          control={form.control}
          name="MonthlyPaperCheckCount"
          rules={{
            ...requiredText('Est. paper checks received per month'),
          }}
          render={({ field }) => (
            <Select
              label="Est. paper checks received per month"
              placeholder="Select..."
              options={MONTHLY_PAPER_CHECK_COUNT_OPTIONS}
              value={field.value === undefined || field.value === null ? '' : String(field.value)}
              onChange={(e) => {
                const v = e.target.value;
                field.onChange(v === '' ? undefined : v);
              }}
              onBlur={field.onBlur}
              name={field.name}
              errors={form.formState.errors as FieldErrors}
              className="with-subtext"
            />
          )}
        />
      </div>

      <div className="signup-form-row">
        <FormTextField
          label="Est. largest individual transaction size"
          type="number"
          className="with-subtext"
          {...form.register('MaximumSingleTransactionAmount', {
            ...requiredText('Est. largest individual transaction size'),
            max: { value: 999999999, message: 'Maximum amount is $999,999,999' },
            ...patternRule(/^\d+$/, 'Please enter a valid whole number (e.g., 1000).'),
            validate: {
              isPositive: (value) => {
                const num = typeof value === 'string' ? Number.parseFloat(value) : value;
                return (num && num > 0) || 'Amount must be greater than zero.';
              },
            },
            ...trimString,
          })}
          errors={form.formState.errors.MaximumSingleTransactionAmount}
        />
        <FormTextField
          label="Est. monthly credit card volume"
          type="number"
          className="with-subtext"
          {...form.register('MonthlyCreditCardVolume', {
            ...requiredText('Est. monthly credit card volume'),
            max: { value: 999999999, message: 'Maximum amount is $999,999,999' },
            ...patternRule(/^\d+$/, 'Please enter a valid whole number (e.g., 10000).'),
            validate: {
              isPositive: (value) => {
                const num = typeof value === 'string' ? Number.parseFloat(value) : value;
                return (num && num > 0) || 'Amount must be greater than zero.';
              },
            },
            ...trimString,
          })}
          errors={form.formState.errors.MonthlyCreditCardVolume}
        />
      </div>

      <div className="signup-form-header">
        <h1 className="signup-form-section-header">Account Plan</h1>
      </div>

      <Controller
        name="PlanTypeId"
        control={form.control}
        rules={{
          required: 'Please select an account plan.',
        }}
        render={({ field, fieldState }) => {
          const selectedPlanString = field.value ? planTypeToString(field.value) : '';
          const hasPlanError = !!fieldState.error;

          return (
            <>
              <div className="signup-form-row">
                {PLAN_OPTIONS.map((plan) => (
                  <AccountPlan
                    key={plan.value}
                    name={field.name}
                    title={plan.title}
                    value={plan.value}
                    checked={selectedPlanString === plan.value}
                    hasError={hasPlanError}
                    inputProps={{
                      name: field.name,
                      onChange: (e) => {
                        const value = e.target.value;
                        const planType = stringToPlanType(value);
                        field.onChange(planType);
                      },
                      onBlur: field.onBlur,
                    }}
                  />
                ))}
              </div>

              {fieldState.error && (
                <InputError error={{ message: fieldState.error.message as string }} name="PlanTypeId" />
              )}
            </>
          );
        }}
      />

      <FormTextField
        label="Any special requests as we set up your account? (Optional)"
        className="with-subtext"
        {...form.register('Comments', {
          ...trimString,
          ...maxLengthRule(1500),
        })}
        errors={form.formState.errors.Comments}
      />

      <div className="signup-form-row">
        <FormTextField
          label="How did you hear about us? (Optional)"
          className="with-subtext"
          {...form.register('DiscoverySource', {
            ...trimString,
            ...maxLengthRule(500),
          })}
          errors={form.formState.errors.DiscoverySource}
        />
        <FormTextField
          label="Promotional Code (Optional)"
          className="with-subtext"
          {...form.register('PromotionalCode', {
            ...trimString,
            ...maxLengthRule(100),
          })}
          errors={form.formState.errors.PromotionalCode}
        />
      </div>

      <CheckBox
        id="signup-authorization"
        inline={true}
        label={
          <>
            I agree with the ePayPolicy{' '}
                <Button
                    variant={ButtonVariants.link}
              onClick={() => window.open('https://epay3.com/terms/', '_blank')}
              style={{ margin: 0, padding: 0, whiteSpace: 'nowrap', display: 'inline' }}
            >
              Terms and Conditions
            </Button>
          </>
        }
        errors={form.formState.errors as FieldErrors}
        {...form.register('Authorization', {
          validate: {
            isValid: (value) => {
              return value || 'You must agree to the terms and conditions.';
            },
          },
        })}
      />

      <CheckBox
        id="signup-privacy"
        inline={true}
        label={
          <>
            I agree with the ePayPolicy{' '}
                <Button
                    variant={ButtonVariants.link}
              onClick={() => window.open('https://epay3.com/privacy/', '_blank')}
              style={{ margin: 0, padding: 0, whiteSpace: 'nowrap', display: 'inline' }}
            >
              Privacy Policy
            </Button>
          </>
        }
        errors={form.formState.errors as FieldErrors}
        {...form.register('PrivacyPolicyAgreement', {
          validate: {
            isValid: (value) => {
              return value || 'You must agree to the privacy policy.';
            },
          },
        })}
      />

      <div className="account-conditions">
        <span>After finishing the application, you will be prompted to upload required documents.</span>
      </div>
    </div>
  );
};
